import type { UseMutateAsyncFunction, UseMutationResult } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'
import type { Offer, Variant } from 'BrokerTsLib/src/dtos'
import { OfferType } from 'BrokerTsLib/src/dtos'
import { Insurer, Product, Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import type { GenericMutationOptions } from 'BrokerTsLib/src/hooks'
import { usePriceFilter } from 'BrokerTsLib/src/hooks'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useApp } from '../hooks/useApp'
import type { CalculateOffersRequest } from '../hooks/useOffer'
import { useSetOfferIncluded } from '../hooks/useOffer'
import { useAuth } from './AuthContext'
import type { CalcFinishedActionBase } from './CommonSignalRProvider'
import { useCommonSignalR } from './CommonSignalRProvider'
import { useInsuranceParams } from './InsuranceParamsProvider'
import { usePrintContext } from './PrintContext'

export const sortByValid = <T extends Offer>(offers: T[], sortFc: (a: T, b: T) => number) => {
  return offers.sort((a, b) => {
    const validDiff = a.errors.length - b.errors.length

    if (validDiff) return validDiff

    return sortFc(a, b)
  })
}

const sortByPriceOnly = <T extends Offer>(a: T, b: T) =>
  !!a.price && (!b.price || a.price < b.price) ? -1 : 1

export const sortByPrice = <T extends Offer>(offers: T[], preferKoopOffers?: boolean) => {
  return sortByValid(offers, (a, b) => {
    if (preferKoopOffers) {
      const aIsKoop = a.insurerId == Insurer.Kooperativa ? -1 : 0
      const bIsKoop = b.insurerId == Insurer.Kooperativa ? -1 : 0
      const diff = aIsKoop - bIsKoop
      if (diff) return diff
    }

    return sortByPriceOnly(a, b)
  })
}

const sortByInsurerOnly = <T extends Offer>(a: T, b: T) =>
  a.insurerName.localeCompare(b.insurerName)

export const sortByInsurer = <T extends Offer>(offers: T[], preferKoopOffers?: boolean) => {
  return sortByValid(offers, (a, b) => {
    if (preferKoopOffers) {
      const aIsKoop = a.insurerId == Insurer.Kooperativa ? -1 : 0
      const bIsKoop = b.insurerId == Insurer.Kooperativa ? -1 : 0
      const diff = aIsKoop - bIsKoop
      if (diff) return diff
    }

    return sortByInsurerOnly(a, b)
  })
}

export const getRiskVariantIdFromVariant = (variants: Variant[], insuranceVariantId: number, riskId: number) => {
  return variants.find(x => x.id == insuranceVariantId)?.variantParts?.find(x => x.risk == riskId)?.riskVariant
}

export const SortBy = {
  Price: 'price',
  Insurer: 'insurer',
  Variant: 'variant'
} as const

export type SortBy = typeof SortBy[keyof typeof SortBy]

interface IRiskVariantFilter {
  [key: number]: number[]
}

export interface IFilter {
  displayOnlyInsurers: number[]
  displayOnlyRiskVariants: IRiskVariantFilter
  displayOnlyRisks: number[]
  displayOnlyFixed: boolean
  displayOnlyFloat: boolean
  displayOnlyFixedComplicity: boolean
  displayOnlyPercentageComplicity: boolean
}

export type IOffersContext<T extends Offer> = {
  sortBy: SortBy
  offers: T[]
  isLoading: boolean
  calculatingOffersIds: number[]
  showAll: boolean
  showWithErrors: boolean
  showFilter: boolean
  isFetchedOffers: boolean
  priceRange: [number, number]
  priceRangeFilter: [number, number]

  setShowFilter: (showFilter: boolean) => void
  setShowAll: (showAll: boolean) => void
  setShowWithErrors: (showWithErrors: boolean) => void
  setSortBy: (data: SortBy) => void
  recalculateOffers: (offerIds?: number[], force?: boolean) => Promise<AxiosResponse<T[], any>>
  updateOffer: ((offer: T) => void)
  setOfferIncluded: ((offerId: number, included: boolean) => Promise<void>)
  refetchOffers: UseMutateAsyncFunction<AxiosResponse<T[], any>, any, GetOffersParams, any>

  filtered: T[]
  handleChangeFilterInsurer: (insurerId: number, filter: boolean) => void
  handleChangeFilterFixed: (fixed?: boolean, float?: boolean) => void
  handleChangeFilterFixedComplicity: (fixed?: boolean, percentage?: boolean) => void
  handleChangeFilterRiskVariant: (riskId: number, riskVariantId: number, filter: boolean) => void
  handleChangeFilterRisk: (riskId: number, filter: boolean) => void
  handleChangeFilterPriceRange: (low: number, high: number) => void
  handleIncludeFiltered: (exclusive: boolean) => void

  filter: IFilter
  resetFilter: () => void
  filterAplied: boolean
}

export const BaseOfferProviderDefaults: IOffersContext<Offer> = {
  sortBy: SortBy.Price,
  offers: [],
  isLoading: false,
  calculatingOffersIds: [],
  showAll: false,
  showWithErrors: false,
  isFetchedOffers: false,
  priceRange: [0, 0],
  priceRangeFilter: [0, 0],
  showFilter: false,

  setShowFilter: () => { },
  setShowAll: () => { },
  setShowWithErrors: () => { },
  setSortBy: () => { },
  recalculateOffers: () => { return null as any },
  updateOffer: () => { },
  setOfferIncluded: () => Promise.resolve(),
  refetchOffers: () => { return null as any },

  filtered: [],
  handleChangeFilterInsurer: () => { },
  handleChangeFilterFixed: () => { },
  handleChangeFilterFixedComplicity: () => { },
  handleChangeFilterRiskVariant: () => { },
  handleChangeFilterRisk: () => { },
  handleChangeFilterPriceRange: () => { },
  handleIncludeFiltered: () => { },

  filter: {
    displayOnlyInsurers: [],
    displayOnlyRiskVariants: {},
    displayOnlyRisks: [],
    displayOnlyFixed: false,
    displayOnlyFloat: false,
    displayOnlyFixedComplicity: false,
    displayOnlyPercentageComplicity: false
  },
  resetFilter: () => { },
  filterAplied: false
}

export type BaseOffersProviderProps = {
  children: ReactNode
}

export type GetOffersParams = { force?: boolean } | undefined

type OffersProviderProps<T extends Offer> = {
  signalRCalcFinishedEventName: CalcFinishedActionBase
  useGetOffers(paramsId: string, options?: GenericMutationOptions<T[]>): UseMutationResult<AxiosResponse<T[], any>, any, GetOffersParams, any>
  useCalculateOffers(): UseMutationResult<AxiosResponse<T[], any>, unknown, CalculateOffersRequest, unknown>
  context: React.Context<IOffersContext<T>>
  defaults: IOffersContext<T>
} & BaseOffersProviderProps

export function OffersProvider<T extends Offer>({
  children,
  useGetOffers,
  useCalculateOffers,
  context,
  signalRCalcFinishedEventName,
  defaults
}: OffersProviderProps<T>) {
  const { product } = useApp()

  const { insuranceParamsId, isContracted } = useInsuranceParams()
  const signalR = useCommonSignalR()
  const hubId = signalR.connection?.connectionId ?? null

  const { user } = useAuth()

  const [offersIds, setOfferIds] = useState<number[]>([])
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.Price)
  const [offers, setOffers] = useState<T[]>([])
  const [calculatingOffersIds, setCalculatingOffersIds] = useState<number[]>([])
  const [showAll, setShowAll] = useState(defaults.showAll)
  const [showWithErrors, setShowWithErrors] = useState(defaults.showWithErrors)
  const [showFilter, setShowFilter] = useState(defaults.showFilter)

  // used for filter
  const [displayOnlyRiskVariants, setDisplayOnlyRiskVariants] = useState<IRiskVariantFilter>([])
  const [displayOnlyRisks, setDisplayOnlyRisks] = useState<number[]>([])
  const [displayOnlyInsurers, setDisplayOnlyInsurers] = useState<number[]>([])
  const [displayOnlyFixed, setDisplayOnlyFixed] = useState(false)
  const [displayOnlyFloat, setDisplayOnlyFloat] = useState(false)
  const [displayOnlyFixedComplicity, setDisplayOnlyFixedComplicity] = useState(false)
  const [displayOnlyPercentageComplicity, setDisplayOnlyPercentageComplicity] = useState(false)
  const { isApplied: isPriceFilterApplied, priceRangeFilter, setPriceRangeFilter, priceFilterFunc, resetPriceFilter, priceRange } = usePriceFilter(offers)

  const updateOffer = (offer: T) => {
    setOffers(prev => {
      const index = prev.findIndex(x => x.offerId === offer.offerId)
      if (index >= 0) {
        const updated = [...prev]
        updated[index] = offer
        return updated
      }

      return [...prev, offer]
    })
  }

  const { mutateAsync: setOfferIncludedAsync } = useSetOfferIncluded()

  const setOfferIncluded = (offerId: number, included: boolean) =>
    setOfferIncludedAsync({ included, offerId })
      .then(() => setOffers(prev => {
        const index = prev.findIndex(x => x.offerId == offerId)
        if (index >= 0) {
          prev[index] = { ...prev[index], included }
          return [...prev]
        }

        return [...prev]
      }))

  // ziskani / zajisteni vytvoreni offers
  const { mutateAsync: getOffers, isPending: isLoadingOffers, isSuccess: isFetchedOffers } = useGetOffers(insuranceParamsId!, {
    retry: false,
    onSuccess: (({ data }) => {
      setOfferIds(data.map(o => o.offerId) ?? [])

      if (isContracted)
        setOffers(data)
    })
  })

  // zavolani kalkulace / rekalkulace
  const { mutateAsync: calculateOffers, isPending: isCalculating } = useCalculateOffers()

  const calculate = useCallback((ids?: number[], force: boolean = false) => {
    setCalculatingOffersIds(ids ?? offers.map(x => x.offerId))
    return calculateOffers({ hubId, offerIds: ids, paramsId: insuranceParamsId, forceCalculation: force ?? false })
  }, [hubId, insuranceParamsId, offers])

  // ziskani kalkulaci
  useEffect(() => {
    if (!!hubId && offersIds.length > 0 && !isContracted)
      calculate(offersIds)
  }, [hubId, offersIds, isContracted])

  // kalkulace se vraci postupne pres sIgnalR
  useEffect(() => {
    signalR.register(signalRCalcFinishedEventName, (offer: any) => {
      setCalculatingOffersIds(prev => [...prev.filter(x => x != offer.offerId)])
      updateOffer(offer)
    })
    return () => signalR.unregister(signalRCalcFinishedEventName)
  }, [signalR])

  const handleChangeFilterInsurer = (insurerId: number, filter: boolean) => {
    if (filter)
      setDisplayOnlyInsurers([...displayOnlyInsurers, insurerId])
    else
      setDisplayOnlyInsurers([...displayOnlyInsurers.filter(x => x != insurerId)])
  }

  const handleChangeFilterFixed = (fixed?: boolean, float?: boolean) => {
    if (fixed != undefined)
      setDisplayOnlyFixed(fixed)

    if (float != undefined)
      setDisplayOnlyFloat(float)
  }

  const handleChangeFilterFixedComplicity = (fixed?: boolean, percentage?: boolean) => {
    if (fixed != undefined)
      setDisplayOnlyFixedComplicity(fixed)

    if (percentage != undefined)
      setDisplayOnlyPercentageComplicity(percentage)
  }

  const handleChangeFilterRiskVariant = (riskId: number, riskVariantId: number, filter: boolean) => {
    const riskItems = displayOnlyRiskVariants[riskId] ?? []

    // pokud uz se podle riskVarianty filtruje, tak nic neresim
    if (filter && riskItems.findIndex(x => x == riskVariantId) >= 0)
      return

    if (filter)
      setDisplayOnlyRiskVariants({ ...displayOnlyRiskVariants, [riskId]: [...riskItems, riskVariantId] })
    else
      setDisplayOnlyRiskVariants({ ...displayOnlyRiskVariants, [riskId]: [...riskItems.filter(x => x != riskVariantId)] })
  }

  const handleChangeFilterRisk = (riskId: number, filter: boolean) => {
    // pokud uz se podle riskVarianty filtruje, tak nic neresim
    if (filter && displayOnlyRisks.findIndex(x => x == riskId) >= 0)
      return

    if (filter)
      setDisplayOnlyRisks([...displayOnlyRisks, riskId])
    else
      setDisplayOnlyRisks([...displayOnlyRisks.filter(x => x != riskId)])
  }

  const handleChangeFilterPriceRange = (low: number, high: number) => {
    if (low || high) {
      setPriceRangeFilter([low, high])
    }
  }

  const { printMode } = usePrintContext()

  const handleIncludeFiltered = (exclusive: boolean) => {
    for (const offer of offers) {
      if (filter(offer)) {
        if (printMode) {
          setOffers(prev => {
            const index = prev.findIndex(x => x.offerId == offer.offerId)
            if (index >= 0) {
              prev[index] = { ...prev[index], print }
              return [...prev]
            }
            return [...prev]
          })
        }
        else {
          // NOTE: Not used anymore.
          setOfferIncluded(offer.offerId, true)
        }
      }
      else {
        if (printMode && exclusive) {
          setOffers(prev => {
            const index = prev.findIndex(x => x.offerId == offer.offerId)
            if (index >= 0) {
              prev[index] = { ...prev[index], print: false }
              return [...prev]
            }
            return [...prev]
          })
        }
      }
    }
  }

  const applyIncludedFilter = (quote: T) => {
    return showAll || quote.included
  }

  const applySelectedFilter = (quotes: T[]) => {
    const selectedOffers = offers.filter(x => x.selected)

    if (selectedOffers.length === 0)
      return quotes

    let filteredOffers = [...quotes]
    for (const selectedOffer of selectedOffers) {
      filteredOffers = filterOffersCombination(selectedOffer, filteredOffers)
    }

    return [...selectedOffers, ...filteredOffers]
  }

  const filterOffersCombination = useCallback((selectedOffer: T, filteredOffers: T[]) => {
    // 1. Kontrola POV a HAV
    // K vybrané offer nesmí být přidaná jiná offer s produktem POV nebo HAV, pokud již obsahuje
    let result = [...filteredOffers]
    if (selectedOffer.hasPov) {
      result =
        result
          .filter(x => x.offerId !== selectedOffer.offerId)
          .filter(x => !x.hasPov)
    }

    if (selectedOffer.hasHav) {
      result =
        result
          .filter(x => x.offerId !== selectedOffer.offerId)
          .filter(x => !x.hasHav)
    }

    // 2. Kontrola doplňků (s výjimkou asistence)
    // Nesmí se opakovat doplňky (TypeId === 3) kromě asistence
    // Vytvoření unikátního seznamu (Distinct)
    const selectedAddonRiskIds = [
      ...new Set(selectedOffer.parts
        .filter(p => p.riskType === 3 && p.riskId !== Risk.ASISTENCE)
        .map(p => p.riskId))
    ]

    if (selectedAddonRiskIds.length > 0) {
      result = result.filter(o =>
        o.parts
          .filter(p => p.riskId !== Risk.ASISTENCE)
          .every(p => !selectedAddonRiskIds.includes(p.riskId))
      )
    }

    // 3. Kontrola nastavení partnera (více pojistitelů)
    // Pokud partner nemá povoleno sjednávat více pojistitelů hlavních produktů najednou
    if (!user?.enableContractMultipleInsurers) {
      result = result.filter(x => x.insurerId === selectedOffer.insurerId)
    }

    return result
  }, [user])

  const hasRiskVariant = (quote: T, riskVariantId: number) => {
    const isMatch = quote.parts.some(p => p.defaultRiskVariantId == riskVariantId)

    // specifikum pro offery bez rizika - bud riziko nema, nebo ma partu s riskVariantou bez rizika
    const withoutPOV = riskVariantId === RiskVariant.WithoutPOV && quote.parts.every(p => p.riskId !== Risk.POV)
    const withoutHAV = riskVariantId === RiskVariant.WithoutHAV && quote.parts.every(p => p.riskId !== Risk.HAV)
    const withoutGlassInsurance = riskVariantId === RiskVariant.WithoutGlassInsurance && quote.parts.every(p => p.riskId !== Risk.SKLA)

    return isMatch || withoutPOV || withoutHAV || withoutGlassInsurance
  }

  const applyRiskVariantsFilter = (quote: T) => {
    return Object
      .keys(displayOnlyRiskVariants)
      .every(riskId => {
        const riskSpecificDisplayOnlyRiskVariants = displayOnlyRiskVariants[Number(riskId)] ?? []

        if (riskSpecificDisplayOnlyRiskVariants.length == 0) return true

        return riskSpecificDisplayOnlyRiskVariants.some(riskVariantId => hasRiskVariant(quote, riskVariantId))
      })
  }


  const applyRisksFilter = (quote: T) => {
    return displayOnlyRisks.length === 0 ||
      displayOnlyRisks.every(rId =>
        quote.parts.some(op => op.riskId === rId)
      )
  }

  const applyInsurersFilter = (quote: T) => {
    return displayOnlyInsurers.length == 0 || displayOnlyInsurers.indexOf(quote.insurerId) >= 0
  }

  const applyFixedFilter = (quote: any) => {
    if (!displayOnlyFixed && !displayOnlyFloat)
      return true

    return displayOnlyFixed && quote.fixedPrice
      || displayOnlyFloat && !quote.fixedPrice
  }

  const applyFixedComplicityFilter = (quote: T) => {
    const complicityPart = quote.parts.find(x => x.riskId == Risk.SPOL)
    if (!complicityPart || (!displayOnlyFixedComplicity && !displayOnlyPercentageComplicity))
      return true

    return (displayOnlyFixedComplicity && complicityPart?.complicityFix)
      || (displayOnlyPercentageComplicity && complicityPart?.complicityPercentage && complicityPart?.complicityPercentage != '0')
  }

  const applyShowWithErrorsFilter = (quote: T) => {
    return showWithErrors || !quote.errors || quote.errors.length == 0
  }

  const filter = (quote: T) => {
    const baseFilter = applyIncludedFilter(quote)
      && applyRisksFilter(quote)
      && applyRiskVariantsFilter(quote)
      && applyInsurersFilter(quote)
      && applyFixedComplicityFilter(quote)
      && priceFilterFunc(quote)
      && applyShowWithErrorsFilter(quote)

    if (product == Product.VehicleLeasingInsurance)
      return baseFilter && applyFixedFilter(quote)

    return baseFilter
  }

  const sort = (offers: T[]) => {
    switch (sortBy) {
      case SortBy.Price:
        return [
          ...sortByPrice(offers.filter(x => x.type == OfferType.KoopSalon)),
          ...sortByPrice(offers.filter(x => x.type != OfferType.KoopSalon), user?.preferKoopOffers)
        ]

      case SortBy.Insurer:
        return sortByInsurer(offers, user?.preferKoopOffers)

      case SortBy.Variant:
        return offers /*TODO*/

      default:
        return offers
    }
  }

  // filter first, so that sorting is faster
  const filtered = applySelectedFilter(sort(offers.filter(filter)))

  const showAllAsFilter = [Product.PropertyInsurance, Product.TravelInsurance].includes(product!) && showAll

  const filterAplied = useMemo(() => {
    return (displayOnlyInsurers?.length ?? 0) > 0
      || displayOnlyRisks.length > 0
      || displayOnlyFixed
      || displayOnlyFloat
      || displayOnlyFixedComplicity
      || displayOnlyPercentageComplicity
      || Object.keys(displayOnlyRiskVariants).length > 0 && Object.keys(displayOnlyRiskVariants).some(x => displayOnlyRiskVariants[Number(x)].length > 0)
      || showWithErrors
      || showAllAsFilter
      || isPriceFilterApplied
  }, [showAllAsFilter, showWithErrors, displayOnlyInsurers, displayOnlyRiskVariants, displayOnlyRisks, displayOnlyFixed, displayOnlyFloat, displayOnlyFixedComplicity, displayOnlyPercentageComplicity, isPriceFilterApplied])

  return (
    <context.Provider value={{
      sortBy,
      offers: offers ?? [],
      isLoading: isLoadingOffers || (offers?.filter(x => x.included)?.length ?? 0) == 0 && isCalculating,
      calculatingOffersIds,
      showAll,
      showWithErrors,
      isFetchedOffers: isFetchedOffers,
      priceRangeFilter,
      priceRange,
      showFilter,

      setShowFilter: setShowFilter,
      setSortBy: setSortBy,
      recalculateOffers: calculate,
      updateOffer,
      setOfferIncluded,
      setShowAll: setShowAll,
      refetchOffers: getOffers,
      setShowWithErrors,

      filtered,
      handleChangeFilterFixed,
      handleChangeFilterInsurer,
      handleChangeFilterRiskVariant,
      handleChangeFilterRisk,
      handleChangeFilterFixedComplicity,
      handleChangeFilterPriceRange,
      handleIncludeFiltered,

      filter: {
        displayOnlyInsurers,
        displayOnlyRiskVariants,
        displayOnlyRisks,
        displayOnlyFixed,
        displayOnlyFloat,
        displayOnlyFixedComplicity,
        displayOnlyPercentageComplicity
      },

      resetFilter: () => {
        setDisplayOnlyInsurers(defaults.filter.displayOnlyInsurers)
        setDisplayOnlyRiskVariants(defaults.filter.displayOnlyRiskVariants)
        setDisplayOnlyRisks(defaults.filter.displayOnlyRisks)
        setDisplayOnlyFixed(defaults.filter.displayOnlyFixed)
        setDisplayOnlyFloat(defaults.filter.displayOnlyFloat)
        setDisplayOnlyFixedComplicity(defaults.filter.displayOnlyFixedComplicity)
        setDisplayOnlyPercentageComplicity(defaults.filter.displayOnlyPercentageComplicity)
        resetPriceFilter()
        setShowWithErrors(defaults.showWithErrors)
      },

      filterAplied
    }}>
      {children}
    </context.Provider>
  )
}